<template>
  <EInstantAnswer
    v-if="show"
    v-analytics="trackAdBlockNoticeImpression()"
    class="notice"
    data-test-id="ad-block-notice"
    :feedback-link="feedbackLinkProps"
  >
    <template #header>
      <div class="notice__header">
        <EHeading as="h2">
          {{ $t("web.adblock.title") }}
        </EHeading>
        <EButton
          v-analytics.click="trackAdBlockNoticeClick('dismiss')"
          icon="close"
          size="s"
          class="modal__close"
          data-test-id="ad-block-notice__close"
          variant="solid-white"
          @click.stop="onClose()"
        />
      </div>
    </template>
    <div class="notice__layout">
      <div class="notice__content" data-test-id="ad-block-notice__content">
        <span class="notice__text">
          {{ $t("web.adblock.text") }}
        </span>
        <ELinkBubble
          v-analytics.click="trackAdBlockNoticeClick('help')"
          :href="disableAdBlockerUrl"
          color="result"
          class="notice__url"
          data-test-id="ad-block-notice__url"
        >
          <EIcon icon="source-ecosia" size="s" />
          {{ $t("web.adblock.cta") }}
        </ELinkBubble>
      </div>
      <img
        class="notice__graphic"
        :src="graphicPath"
        loading="lazy"
        aria-hidden="true"
      >
    </div>
  </EInstantAnswer>
</template>

<script>
import { mapGetters } from 'vuex';
import EButton from '~~/common/vue/components/button';
import EIcon from '~~/common/vue/components/icon';
import ELinkBubble from '~~/common/vue/components/link-bubble';
import EInstantAnswer from '~/components/instant-answers/base';
import EHeading from '~~/common/vue/components/heading';
import graphicPath from '~~/common/vue/assets/images/hand-plant.as-path.svg';
import { HELPSCOUT_AD_BLOCKING } from '~~/common/js/constants/urls';
import { adRevenueExperiment } from '~~/common/js/constants/feature-flags';
import { AD_BLOCK_NOTICE_DISMISSED } from '~~/common/js/constants/storage-keys';
import { checkAdsHidden } from '~~/search/common/js/client/utils/ad-visibility';
import {
  trackAdBlockNoticeImpression,
  trackAdBlockNoticeClick,
} from '~~/search/common/js/client/analytics/ad-block-notice';
import { MEDIUM as TIMEOUT_MEDIUM, GENEROUS as TIMEOUT_GENEROUS } from '~~/common/js/constants/timeouts';

export default {
  name: 'EAdBlockNotice',
  components: {
    EButton,
    EIcon,
    EInstantAnswer,
    EHeading,
    ELinkBubble,
  },
  data() {
    return {
      adBlockDetected: false,
      dismissed: false,
      trackAdBlockNoticeImpression,
      trackAdBlockNoticeClick,
    };
  },
  computed: {
    ...mapGetters('unleash', ['getFeatureToggle']),
    ...mapGetters(['hasTextAds', 'hasProductAds', 'hasTourActivityAds']),
    show() {
      return !this.dismissed && this.adBlockDetected && this.flagActive;
    },
    flagActive() {
      // using variant a of our long running a/b test to roll out ad block notice
      return this.getFeatureToggle(adRevenueExperiment.flag)?.value ===
             adRevenueExperiment.variantA;
    },
    feedbackLinkProps() {
      return {
        errorMessage: this.$t('common.header.feedback.error'),
        options: [
          { label: this.$t('common.header.feedback.bullet1'), value: 'helpful' },
          { label: this.$t('common.header.feedback.bullet2'), value: 'missing' },
          { label: this.$t('common.header.feedback.bullet3'), value: 'wrong' },
          { label: this.$t('common.header.feedback.bullet4'), value: 'not_helpful' },
        ],
        query: this.$route ? this.$route.query?.q?.toString() : '',
        submitText: this.$t('common.header.feedback.send'),
        successMessage: this.$t('common.header.feedback.body2'),
        successTitle: this.$t('common.header.feedback.title2'),
        textareaPlaceholder: this.$t('common.header.feedback.tellmore'),
        title: this.$t('common.header.feedback.login'),
        ticketSubject: 'Search - Ad blocker notice',
        url: this.$config.feedbackUrl,
        widgetType: 'adblock_notice',
      };
    },
    disableAdBlockerUrl() {
      return HELPSCOUT_AD_BLOCKING;
    },
    graphicPath() {
      return graphicPath;
    },
  },
  mounted() {
    this.dismissed = localStorage?.getItem(AD_BLOCK_NOTICE_DISMISSED) === '1';

    const handleDetectionCheck = () => {
      // we only consider detection if we received ads in the store, but they are not visible.
      const { textAdsHidden, productAdsHidden, tourActivityAdsHidden } = checkAdsHidden();
      this.adBlockDetected = (this.hasTextAds && textAdsHidden) ||
        (this.hasProductAds && productAdsHidden) ||
        (this.hasTourActivityAds && tourActivityAdsHidden);
    };

    // we check initially, but also again after a certain amount of time,
    // because some browsers / ad blockers seem to not instantly block ads
    handleDetectionCheck();
    setTimeout(handleDetectionCheck, TIMEOUT_MEDIUM);
    setTimeout(handleDetectionCheck, TIMEOUT_GENEROUS);
  },
  methods: {
    onClose() {
      this.dismissed = true;
      localStorage?.setItem(AD_BLOCK_NOTICE_DISMISSED, '1');
    },
  },
};
</script>
<style lang="scss" scoped>
.notice {
  @include desktop-down {
    display: none;
  }
}

.notice__header {
  display: flex;
  justify-content: space-between;
  padding: $space-m $space-m 0;
}

.notice__layout {
  display: flex;
  flex-direction: row;
  gap: $space-m;
}

.notice__content {
  display: flex;
  flex-direction: column;
  gap: $space-m;
}

.notice__url {
  justify-content: flex-start;
  align-self: flex-start;
  gap: $space-2s;
}

.notice__graphic {
  width: $space-8l;
  height: $space-8l;
}
</style>
